/**
 * Black Friday countdown with rolling digits.
 * Every [data-bf-countdown] element counts down to its data-start (before the sale) or to its data-end (during the sale),
 * both ISO dates with a time zone offset, e.g. 2026-10-26T00:00:00+02:00.
 * It sets data-state ("before" | "live" | "ended") and data-timer ("on" | "off"); the CSS shows the right texts.
 * The server already renders the right numbers, so the page is correct without this script; the script swaps each
 * number for odometer-style columns: on load the digits scroll into place, then they roll whenever they change.
 */
(function () {
  var CYCLES = 3; // one column holds 0-9 three times so the intro can spin through whole turns
  var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function parse(value) {
    var t = Date.parse(value || "");
    return isNaN(t) ? null : t;
  }

  // ---- One digit column -------------------------------------------------
  function makeColumn() {
    var col = document.createElement("span");
    col.className = "bf-roll__col";
    col.setAttribute("aria-hidden", "true");
    var strip = document.createElement("span");
    strip.className = "bf-roll__strip";
    for (var i = 0; i < 10 * CYCLES + 1; i++) {
      var cell = document.createElement("span");
      cell.textContent = String(i % 10);
      strip.appendChild(cell);
    }
    col.appendChild(strip);
    var api = { el: col, strip: strip, digit: 0, index: 0 };

    function place(index, animate, duration, delay) {
      strip.style.transitionDuration = animate ? duration + "ms" : "0ms";
      strip.style.transitionDelay = animate ? (delay || 0) + "ms" : "0ms";
      strip.style.setProperty("--i", index);
      api.index = index;
    }
    // After a long roll, jump back to the first cycle without animating (same digit, so nothing visible changes).
    strip.addEventListener("transitionend", function (e) {
      if (e.target !== strip || api.index < 10) return;
      void strip.offsetHeight;
      place(api.digit, false);
    });
    api.set = function (digit, opts) {
      opts = opts || {};
      if (opts.instant || reduced) {
        api.digit = digit;
        place(digit, false);
        return;
      }
      if (opts.intro) {
        place(0, false);
        void strip.offsetHeight;
        api.digit = digit;
        place(10 * (CYCLES - 1) + digit, true, opts.duration || 1900, opts.delay || 0);
        return;
      }
      if (digit === api.digit) return;
      // 9 to 0 keeps rolling forward onto the next 0 instead of spinning back through the whole column.
      var forward = digit === 0 && api.digit === 9;
      var inIntro = api.index >= 10; // still scrolling into place: retarget the same long roll
      var base = 10 * (CYCLES - 1);
      api.digit = digit;
      if (inIntro) place(forward ? base + 10 : base + digit, true, 650);
      else place(forward ? 10 : digit, true, 650);
    };
    return api;
  }

  // ---- One number (two digits, or more for big day counts) -------------
  function makeNumber(host) {
    var cols = [];
    var api = {
      host: host,
      cols: cols,
      write: function (value, opts) {
        var text = value < 10 ? "0" + value : String(value);
        while (cols.length < text.length) {
          var c = makeColumn();
          cols.push(c);
          host.appendChild(c.el);
        }
        for (var i = 0; i < text.length; i++) {
          var o = opts || {};
          var next = { instant: o.instant, intro: o.intro, duration: o.duration, delay: o.intro ? (o.delay || 0) + i * 90 : 0 };
          cols[i].set(Number(text.charAt(i)), next);
        }
        host.setAttribute("aria-label", text);
      },
    };
    host.textContent = "";
    host.setAttribute("role", "img");
    host.classList.add("bf-roll");
    return api;
  }

  function setup(root) {
    if (root._bfTimer) clearTimeout(root._bfTimer);
    var start = parse(root.dataset.start);
    var end = parse(root.dataset.end);
    var numbers = {};
    ["days", "hours", "minutes", "seconds"].forEach(function (name) {
      var host = root.querySelector('[data-bf-unit="' + name + '"]');
      if (host) numbers[name] = makeNumber(host);
    });
    var order = ["days", "hours", "minutes", "seconds"];
    var firstPaint = true;
    var lastState = null;
    var force = root.dataset.force || "";
    var reloading = false;

    function scheduleReload() {
      if (reloading) return;
      reloading = true;
      // A little random wait so thousands of open pages do not all ask at the same second.
      setTimeout(function () {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set("bf", String(Date.now()));
          window.location.replace(url.toString());
        } catch (e) {
          window.location.reload();
        }
      }, 1200 + Math.random() * 6000);
    }

    function tick() {
      if (!root.isConnected) return; // the theme re-rendered this block (e.g. variant change): a new copy is set up
      var now = Date.now();
      var state, target;
      if (force === "live") { state = "live"; target = end; }
      else if (force === "before") { state = "before"; target = start; }
      else if (force === "ended") { state = "ended"; target = null; }
      else if (start !== null && now < start) { state = "before"; target = start; }
      else if (end !== null && now >= end) { state = "ended"; target = null; }
      else { state = "live"; target = end; }

      // Pages with Black Friday blocks that only exist on the server (data-bf-reload) get a fresh copy when the state
      // changes, so the sale content and the sale prices show up without the visitor doing anything.
      if (lastState !== null && state !== lastState && !force && root.hasAttribute("data-bf-reload")) {
        scheduleReload();
      }
      lastState = state;

      root.dataset.state = state;
      root.dataset.timer = target === null ? "off" : "on";
      // Optional "last hours" message (data-last-hours on the element): on during the final hours of the sale.
      var lastHours = parseFloat(root.dataset.lastHours || "");
      if (lastHours > 0 && state === "live" && target !== null && target - now < lastHours * 3600000) root.dataset.last = "true";
      else delete root.dataset.last;

      if (target !== null) {
        var s = Math.max(0, Math.floor((target - now) / 1000));
        var values = {
          days: Math.floor(s / 86400),
          hours: Math.floor((s % 86400) / 3600),
          minutes: Math.floor((s % 3600) / 60),
          seconds: s % 60,
        };
        order.forEach(function (name, n) {
          if (!numbers[name]) return;
          numbers[name].write(values[name], firstPaint ? { intro: true, delay: 250 + n * 160, duration: 1700 + n * 250 } : null);
        });
        firstPaint = false;
        root.dataset.ready = "true";
      }
      if (state === "ended") return;
      // Aim at the next whole second so the digits change together with the clock.
      root._bfTimer = setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
    }

    tick();
    if (!root._bfVisibility) {
      root._bfVisibility = function () {
        if (!document.hidden) { clearTimeout(root._bfTimer); tick(); }
      };
      document.addEventListener("visibilitychange", root._bfVisibility);
    }
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-bf-countdown]"), setup);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
  // Blocks re-rendered by the theme (product page: variant change) come back with server numbers: set them up again.
  if (window.MutationObserver) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        Array.prototype.forEach.call(document.querySelectorAll("[data-bf-countdown]:not([data-ready])"), function (el) {
          if (el.dataset.timer === "on") setup(el);
        });
      });
    }).observe(document.body || document.documentElement, { childList: true, subtree: true });
  }
  // Theme editor: sections are re-rendered live.
  document.addEventListener("shopify:section:load", function (e) {
    Array.prototype.forEach.call(e.target.querySelectorAll("[data-bf-countdown]"), setup);
  });
})();
